import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { toastUtils } from "../../../Utils/helper";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import {
  useGetQuestDetailsQuery,
  usePostFileTakeQuestMutation,
} from "../../../services/apiSlice";
import Button from "../../../components/common/Button/Button";
import CommonModal from "../Modal/ModalCommon";
import images from "../../../images";
import { cn } from "../../../Utils/helper";
import BodyModalLoading from "../Modal/BodyModalLoading/BodyModalLoading";
import Dialog from "../Dialog";
import Lottie from "lottie-react";
import { useUploadProgress } from "../../../hooks/useUploadProgress";

// Constants for reusability
const FILE_ICONS = {
  image: images.IconPNGImage,
  pdf: images.IconPDF,
  default: images.DefaultUploadImage,
};

const STYLES = {
  container:
    "flex flex-col items-center text-xs max-w-[480px] text-zinc-500 relative ",
  label:
    "flex flex-col justify-center items-center w-full bg-white rounded-md min-h-[92px] cursor-pointer relative border-2 border-dashed mt-4",
  button: {
    // base: "flex-1 shrink gap-2.5 px-5 py-2 rounded-lg w-full text-white text-base font-medium border-none mt-[18px]",
    base: "flex  h-[50px] px-[18px] py-[12px] justify-center  w-full items-center gap-[6px] shrink-0 rounded-md text-white  text-base font-medium mt-[18px]",
    disabled: "bg-[#E9E6E4] cursor-not-allowed text-[#A0A0A0]",
    enabled: "bg-[#253FF6] hover:bg-indigo-700",
  },
  modal: {
    title: "text-[14px] font-medium text-[#253FF6]",
    message:
      "flex flex-col gap-4 text-center items-center text-[#333] text-lg font-medium",
    subMessage: "text-center text-sm text-[#A0A0A0] font-medium px-5",
  },
};

function UploadComponent({
  label = "Upload File",
  onFileUpload,
  maxFiles = 5,
  initialFiles = [],
  handleNextStep = () => {},
  setFileUploaded = () => {},
}) {
  const [files, setFiles] = useState([]);
  const [confirmDeleteFile, setConfirmDeleteFile] = useState(null);
  const [modalContent, setModalContent] = useState(null);
  const [resolution, setResolution] = useState(true);
  const [reUploadLoading, setReUploadLoading] = useState(false);
  // const [showSourceModal, setShowSourceModal] = useState(false);
  const { progress, startProgress, completeProgress, resetProgress } =
    useUploadProgress();
  const [searchParams] = useSearchParams();

  const questId = searchParams.get("quest_id");
  const { t } = useTranslation();
  const modalRef = useRef(null);
  const sourceModalRef = useRef(null);
  const reviewReceiptModalRef = useRef(null);
  const reUploadReceiptModalRef = useRef(null);
  const passReceiptModalRef = useRef(null);
  const filesRef = useRef(files);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const { id } = useParams();
  const { data: questDetailsData } = useGetQuestDetailsQuery(
    { quest_id: questId },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [postFileTakeQuest, { isLoading: uploadLoading }] =
    usePostFileTakeQuestMutation();

  // Use useRef to track files for cleanup

  // Update filesRef whenever files changes
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    setFiles([]);
    setConfirmDeleteFile(null);

    if (Array.isArray(initialFiles) && initialFiles.length > 0) {
      const processedFiles = initialFiles.map((file, index) => {
        const isFileObject = file instanceof File;

        if (isFileObject) {
          return {
            id: `file_${index}_${Date.now()}`,
            name: file.name,
            type: "image",
            file: file,
            preview: URL.createObjectURL(file),
            isLocal: true,
            serverData: null,
          };
        }

        let fileName = "";
        let previewUrl = null;
        let serverData = file;
        if (file?.name) {
          fileName = file.name;
        } else if (file?.file?.name) {
          fileName = file.file.name;
        } else if (file?.file_name) {
          fileName = file.file_name;
        } else if (file?.filename) {
          fileName = file.filename;
        } else {
          fileName = `File_${index + 1}`;
        }
        if (file?.file_path) {
          previewUrl = `${process.env.REACT_APP_URL_IMAGE}/${file.file_path}`;
        } else if (file?.url) {
          previewUrl = file.url;
        } else if (file?.file?.file_path) {
          previewUrl = `${process.env.REACT_APP_URL_IMAGE}/${file.file.file_path}`;
        } else if (file?.file?.url) {
          previewUrl = file.file.url;
        }

        return {
          id: `server_${index}_${Date.now()}`,
          name: fileName,
          type: "image",
          file: null,
          preview: previewUrl,
          isLocal: false,
          serverData: serverData,
        };
      });

      setFiles(processedFiles);
    }

    // Cleanup function for local preview URLs
    return () => {
      filesRef.current.forEach((file) => {
        if (file.preview && file.isLocal && file.preview.startsWith("blob:")) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [initialFiles]);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const imageFiles = selectedFiles.filter((file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        toastUtils.error(`${file.name} ${t("toast.error.isNotAnImage")}`);
      }
      return isImage;
    });

    if (imageFiles.length === 0) return;

    if (imageFiles.length + files.length > maxFiles) {
      toastUtils.warning(
        `${t("uploadComponent.youCanOnlyUploadAMaximumOf")} ${maxFiles} ${t(
          "uploadComponent.file"
        )}`
      );
      return;
    }

    const newFiles = imageFiles.map((file, index) => ({
      id: `new_${Date.now()}_${index}`,
      name: file.name,
      type: "image",
      file,
      preview: URL.createObjectURL(file),
      isLocal: true,
      serverData: null,
    }));

    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);

    if (onFileUpload) {
      const fileObjects = updatedFiles
        .map((f) => f.file || f.serverData)
        .filter(Boolean);
      onFileUpload(fileObjects);
    }

    event.target.value = "";
  };

  const handleDeleteFile = (fileId) => {
    const fileToRemove = files.find((file) => file.id === fileId);
    const updatedFiles = files.filter((file) => file.id !== fileId);

    setFiles(updatedFiles);
    setConfirmDeleteFile(null);

    if (
      fileToRemove?.preview &&
      fileToRemove.isLocal &&
      fileToRemove.preview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(fileToRemove.preview);
    }

    if (onFileUpload) {
      const fileObjects = updatedFiles
        .map((f) => f.file || f.serverData)
        .filter(Boolean);
      onFileUpload(fileObjects);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteFile(null);
  };

  const handleReUpload = () => {
    setReUploadLoading(true);
    reviewReceiptModalRef.current?.closeModal();
    modalRef.current?.closeModal();
    // reUploadReceiptModalRef.current?.openModal();
    files.forEach((file) => {
      if (file.preview && file.isLocal && file.preview.startsWith("blob:")) {
        URL.revokeObjectURL(file.preview);
      }
    });
    setFiles([]);
    setFileUploaded(false);

    if (onFileUpload) {
      onFileUpload([]);
      // setReUploadLoading(false);
    }
  };

  const handlePassReceipt = () => {
    passReceiptModalRef.current?.closeModal();
    handleNextStep();
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      toastUtils.error(t("toast.error.noFilesUploaded"));
      return;
    }

    try {
      startProgress();
      if (reUploadLoading) {
        reUploadReceiptModalRef.current?.openModal();
      } else {
        reviewReceiptModalRef.current?.openModal();
      }
      const formData = new FormData();
      files.forEach((fileItem) => {
        if (fileItem.file instanceof File) {
          formData.append("files", fileItem.file);
        }
      });

      const hasNewFiles = files.some((f) => f.file instanceof File);

      if (!hasNewFiles) {
        setFileUploaded(true);
        handleNextStep();
        return;
      }

      const response = await postFileTakeQuest({
        formData,
        participant_id: id,
      }).unwrap();

      await completeProgress();

      if (
        response.status === 200 &&
        response.data.is_resolution === true &&
        response.data.is_name === true &&
        response.data.is_time === true &&
        response.data.is_location === true &&
        response.data.is_receipt_minimum_spent === true &&
        response.data.is_pass_exist_receipt === true
      ) {
        setResolution(true);
        setFileUploaded(true);
        setReUploadLoading(false);
        reviewReceiptModalRef.current?.closeModal();
        reUploadReceiptModalRef.current?.closeModal();
        // handleNextStep();
        passReceiptModalRef.current?.openModal();
      } else {
        const {
          is_resolution,
          is_name,
          is_time,
          is_location,
          is_receipt_minimum_spent,
          is_pass_exist_receipt,
        } = response.data;
        const errorMessages = [];

        if (is_resolution === false) {
          setResolution(false);
          setModalContent({
            background: "bg-[#F5E3E6]",
            titleKey: "quest.detailMultipleChoice.oops",
            primaryMessageKeys: ["quest.detailMultipleChoice.theResolution"],
            buttonTextKey: "quest.detailMultipleChoice.proceedAnyway",
          });
          setReUploadLoading(false);
          modalRef.current?.openModal();
          reviewReceiptModalRef.current?.closeModal();
          reUploadReceiptModalRef.current?.closeModal();
        } else if (is_pass_exist_receipt === false) {
          setResolution(true);
          setModalContent({
            background: "bg-[#F5E3E6]",
            titleKey: "quest.detailMultipleChoice.oops",
            primaryMessageKeys: [
              "quest.detailMultipleChoice.receiptHasAlreadyBeenUsed",
            ],
            buttonTextKey: "quest.detailMultipleChoice.proceedAnyway",
          });
          setReUploadLoading(false);
          modalRef.current?.openModal();
          reviewReceiptModalRef.current?.closeModal();
          reUploadReceiptModalRef.current?.closeModal();
        } else {
          setResolution(true);
          if (is_name === false) {
            errorMessages.push("quest.detailMultipleChoice.businessNotMatched");
          }
          if (is_location === false) {
            errorMessages.push(
              "quest.detailMultipleChoice.purchaseRejectedLocation"
            );
          }
          if (is_time === false) {
            errorMessages.push("quest.detailMultipleChoice.purchaseRejected");
          }
          if (is_receipt_minimum_spent === false) {
            errorMessages.push(
              t("quest.detailMultipleChoice.purchaseRejectedPrice", {
                minSpend:
                  `$${questDetailsData?.data?.quest_business?.receipt_minimum_spent}` ??
                  `$1`,
              })
            );
          }
          if (errorMessages.length > 0) {
            setModalContent({
              background: "bg-[#F5E3E6]",
              titleKey: "quest.detailMultipleChoice.oops",
              primaryMessageKeys: errorMessages,
              buttonTextKey: "quest.detailMultipleChoice.proceedAnyway",
            });
            setReUploadLoading(false);
            modalRef.current?.openModal();
            reviewReceiptModalRef.current?.closeModal();
            reUploadReceiptModalRef.current?.closeModal();
          }
        }
      }
      // if (response.status === 200) {
      //   setFileUploaded(true);
      //   setResolution(true);
      //   setReUploadLoading(false);
      //   reviewReceiptModalRef.current?.closeModal();
      //   reUploadReceiptModalRef.current?.closeModal();
      //   // handleNextStep();
      //   passReceiptModalRef.current?.openModal();
      // }
    } catch (error) {
      resetProgress();
      reviewReceiptModalRef.current?.closeModal();
      reUploadReceiptModalRef.current?.closeModal();

      const errorMessage =
        error?.data?.message || t("toast.error.noFilesUploaded");
      toastUtils.error(errorMessage);
      console.error("Upload failed:", error);
    }
  };

  return (
    <div className={STYLES.container}>
      {files.length === 0 ? (
        <div className="h-[100px] flex flex-col items-center justify-center w-full border-dashed border border-[#A0A0A0] rounded-[7px] p-1.5">
          {/* Nút Upload duy nhất */}
          <div
            className="flex flex-col items-center py-4 w-full mt-2 cursor-pointer"
            onClick={() => sourceModalRef.current?.open()}
          >
            <img
              loading="lazy"
              src={FILE_ICONS.default}
              className="object-contain w-[26px]"
              alt="Default icon"
            />
            <div className="gap-1.5 self-stretch my-auto mt-1.5 flex justify-center text-[#A0A0A0] text-sm font-medium">
              {label}
            </div>
          </div>
          {/* Inputs ẩn */}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            ref={cameraInputRef}
            onChange={handleFileChange}
          />
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={galleryInputRef}
            onChange={handleFileChange}
          />
        </div>
      ) : null}

      <Dialog ref={sourceModalRef} hiddenCloseButton>
        <div className="flex flex-col gap-4 items-center w-full">
          {/* <div className="text-lg font-semibold text-center">
            {t("uploadComponent.chooseSource")}
          </div> */}
          <Button
            text={t("uploadComponent.takePhoto")}
            className="flex  h-[50px] px-[18px] py-[12px] justify-center items-center gap-0 shrink-0 bg-[#253FF6] text-white rounded-md text-base font-medium"
            onClick={() => {
              sourceModalRef.current?.close();
              cameraInputRef.current.click();
            }}
          />
          {/* className="bg-[#253FF6] text-white px-4 py-2 rounded font-medium w-full" */}

          <Button
            text={t("uploadComponent.chooseFromGallery")}
            className="flex  h-[50px] px-[18px] py-[12px] justify-center items-center gap-0 shrink-0 bg-[#253FF6] text-white rounded-md text-base font-medium"
            onClick={() => {
              sourceModalRef.current?.close();
              galleryInputRef.current.click();
            }}
          />
          <Button
            text={t("base.cancel")}
            className="flex  h-[50px] px-[18px] py-[12px] justify-center items-center gap-0 shrink-0 text-[#253FF6] bg-white rounded-md border-[#253FF6] text-base font-medium"
            onClick={() => sourceModalRef.current?.close()}
          />
        </div>
      </Dialog>

      <CommonModal
        ref={passReceiptModalRef}
        // ref={questDetailDialogRef}
        classNameText="text-[14px] font-medium text-[#253FF6]"
        showCloseIcon={false}
        canClickOverLay={false}
        classNameModal="p-6 bg-[#E0F2EA]"
        classNameBodyModal="p-0"
      >
        <BodyModalLoading
          classNameLoadingContainer="bg-transparent gap-0"
          showLoading={false}
          text={t("quest.questDetails.receiptValidation")}
          imageSrc={images.ReviewingQuestCompletedImage}
          classNameTipsText="text-2xl"
          tipsText1={t("quest.questDetails.allGood")}
          animationSrc={"goodjob.json"}
          buttonText={t("onBoard.letsGo")}
          onButtonClick={handlePassReceipt}
        />
      </CommonModal>

      {files.length === 0 ? (
        <div className="flex flex-col items-center"></div>
      ) : (
        <div className="w-full  flex flex-col gap-3">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex flex-col border-[1px] border-[#EEEAE8] relative overflow-hidden rounded-[7px]"
            >
              {confirmDeleteFile === file.id ? (
                <div className="absolute inset-0 bg-black/50 flex flex-col justify-center items-center z-10 p-4 gap-2">
                  <span className="text-white text-base font-medium text-center">
                    {t("quest.detailMultipleChoice.confirmDelete")}
                  </span>
                  <div className="flex gap-2 w-full justify-center">
                    <Button
                      text={t("base.delete")}
                      onClick={() => handleDeleteFile(file.id)}
                      className="flex-1 shrink gap-0 px-5 py-2 rounded-xl max-w-[110px] bg-white text-[#253FF6] text-base font-medium"
                    />
                    <Button
                      text={t("base.cancel")}
                      className="flex-1 shrink gap-0 px-5 py-2 rounded-xl max-w-[110px] text-white bg-[#253FF6] text-base font-medium border-none"
                      variant="primary"
                      onClick={handleCancelDelete}
                    />
                  </div>
                </div>
              ) : (
                <div className="absolute w-full flex justify-center top-8 z-10">
                  <img
                    src={images.TrashWhiteIcon}
                    onClick={() => setConfirmDeleteFile(file.id)}
                    className="text-white rounded-full p-1 w-8 flex items-center justify-center cursor-pointer"
                    alt="Delete icon"
                  />
                </div>
              )}

              <div className="relative">
                {file.preview ? (
                  <>
                    <img
                      src={file.preview}
                      alt={file.name}
                      className="w-full max-h-[100px] object-cover rounded-md"
                      onError={(e) => {
                        console.error("Image load error:", e, "File:", file);
                        e.target.style.display = "none";
                        const filenameElement = e.target.nextElementSibling;
                        if (filenameElement) {
                          filenameElement.style.display = "block";
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center rounded-[7px]"></div>
                  </>
                ) : null}
                <div
                  className="p-3 text-center text-sm text-gray-600 bg-gray-50"
                  style={{
                    display: file.preview ? "none" : "block",
                  }}
                >
                  <div className="flex flex-col items-center gap-2">
                    <img
                      src={FILE_ICONS.image}
                      alt="File icon"
                      className="w-8 h-8"
                    />
                    <span className="break-all">{file.name}</span>
                    <span className="text-xs text-gray-400">
                      {file.isLocal ? "Local file" : "Uploaded file"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={handleSubmit}
        className={cn(
          `${STYLES.button.base} ${
            uploadLoading || files.length === 0
              ? STYLES.button.disabled
              : STYLES.button.enabled
          }`
        )}
        disabled={uploadLoading || files.length === 0}
      >
        <span className="text-xl">{t("base.review")}</span>
      </button>
      <CommonModal
        ref={reviewReceiptModalRef}
        classNameText="text-[14px] font-medium text-[#253FF6]"
        showCloseIcon={false}
        canClickOverLay={false}
        classNameModal="p-6"
        classNameBodyModal="bg-[#ECEEFF] p-0"
      >
        <BodyModalLoading
          progress={progress}
          text={t("quest.questDetails.rallyTips")}
          loadingText={t("quest.questDetails.validatingReceipt")}
          animationSrc={"filling-survey.json"}
          tipsText1={t("quest.questDetails.shortSharpPerfect")}
          iconText1="🎯"
          conclusionText={t("quest.questDetails.clarityWins")}
        />
      </CommonModal>

      <CommonModal
        ref={modalRef}
        classNameText={STYLES.modal.title}
        showCloseIcon={false}
        canClickOverLay={false}
        closeIcon={images.CloseModalIconGray}
        hasError={true}
        // classNameModal={`p-6 ${modalContent?.background}`}
        classNameModal="p-6 bg-[#F5E3E6]"
        classNameBodyModal="bg-white py-[42px] px-[30px] max-h-[80vh] xs:max-h-[90vh]"
        actions={() => (
          <div className="flex justify-center w-full">
            {modalContent && (
              <span
                onClick={handleNextStep}
                className="text-[#A0A0A0] font-[Figtree] text-[16px] font-medium leading-[24px] underline cursor-pointer"
              >
                {t(modalContent.buttonTextKey)}
              </span>
            )}
          </div>
        )}
      >
        <div
          className={cn(
            modalContent?.primaryMessageKeys?.length > 1
              ? "text-sm sm:text-base md:text-lg flex flex-col items-center bg-white gap-3 xs:gap-6 scrollbar-hide"
              : "flex flex-col items-center gap-3 xs:gap-6 w-full scrollbar-hide"
          )}
        >
          <div>
            <p className="text-[#253FF6] font-bold text-base">
              {t("quest.questDetails.receiptValidation")}
            </p>
          </div>
          <div className="flex flex-col justify-center p-3 xs:p-6 items-center self-stretch  h-[180px] xs:h-[240px] [&>div]:h-full">
            <Lottie
              animationData={require("../../../images/animation/sorry.json")}
            />
            {/* <img
              src={images.ReviewReceiptImage}
              alt="Receipt Illustration"
              className={cn(
                modalContent?.primaryMessageKeys?.length > 1
                  ? "w-[full] h-[full]"
                  : "w-[full] h-[full]"
              )}
            /> */}
          </div>

          <div className="flex flex-col">
            <p className="text-[#253FF6] text-center font-[Figtree] text-xl xs:text-2xl font-medium leading-normal">
              {modalContent
                ? t(modalContent.titleKey)
                : t("quest.questDetails.yourMission")}
            </p>
            <h6 className="text-[#253FF6] text-center font-[Figtree] text-xl xs:text-2xl font-bold  leading-trim-both ">
              {modalContent?.primaryMessageKeys?.length} Issue found
            </h6>
          </div>

          {resolution === false ||
          modalContent?.primaryMessageKeys[0] ===
            "quest.detailMultipleChoice.receiptHasAlreadyBeenUsed" ? (
            <>
              <h6 className="text-[#333333] text-center font-[Figtree] text-sm xs:text-lg font-bold  leading-trim-both">
                {t(modalContent?.primaryMessageKeys[0])}
              </h6>
            </>
          ) : (
            <div className="flex flex-col w-full">
              {modalContent?.primaryMessageKeys?.map((key, index) => (
                <div
                  key={index}
                  className="flex flex-row justify-start w-full px-3  xs:px-4 gap-2 items-start"
                >
                  <h6 className="text-[#333333] text-start font-[Figtree] text-sm xs:text-lg font-normal">
                    {index + 1}.
                  </h6>
                  <h6 className="text-[#333333] text-start font-[Figtree] text-sm xs:text-lg font-normal ">
                    {t(key)}
                  </h6>
                </div>
              ))}
            </div>
          )}
          <Button
            onClick={handleReUpload}
            text={"Re-upload"}
            className="w-full gap-0 mt-0 px-4 py-2 xs:px-[18px] xs:py-3 rounded-md min-h-[54px] bg-[#253FF6] text-white font-[Figtree] text-base xs:text-xl font-medium "
          />

          <p className="text-[#A0A0A0] text-center font-[Figtree] text-[14px] font-medium leading-[21px] ">
            {t("quest.detailMultipleChoice.reportIfIncorrect")}
          </p>
        </div>
      </CommonModal>
      <CommonModal
        ref={reUploadReceiptModalRef}
        classNameText="text-[14px] font-medium text-[#253FF6]"
        showCloseIcon={false}
        closeIcon={images.CloseModalIconGray}
        canClickOverLay={false}
        classNameModal="p-6"
        classNameBodyModal="bg-[#ECEEFF] p-0"
      >
        <BodyModalLoading
          classNameLoadingContainer="bg-[#ECEEFF]"
          progress={progress}
          text={t("quest.questDetails.rallyTips")}
          loadingText={t("quest.questDetails.validatingReceipt")}
          animationSrc={"critical-feedback.json"}
          classNameTipsText="text-2xl"
          tipsText1={t("quest.questDetails.criticalFeedbackHelpsTheMost")}
          iconText1="👍"
          conclusionText={t("quest.questDetails.pointOutProblemsHonestly")}
        />
      </CommonModal>
    </div>
  );
}

UploadComponent.propTypes = {
  label: PropTypes.string,
  onFileUpload: PropTypes.func,
  maxFiles: PropTypes.number,
  initialFiles: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.instanceOf(File),
      PropTypes.shape({
        name: PropTypes.string,
        url: PropTypes.string,
        file: PropTypes.instanceOf(File),
        file_path: PropTypes.string,
      }),
    ])
  ),
  handleNextStep: PropTypes.func,
  setFileUploaded: PropTypes.func,
};

export default UploadComponent;
